import type { TrainingImage, ImageIssue, ValidationWarning, CostEstimate, CreateModelState } from './types'
import type { TrainingModelDef } from './types'
import { MIN_IMAGE_DIMENSION, MAX_FILE_SIZE_MB, MARKUP_MULTIPLIER } from './constants'

// ─── Image Dimension Reading ────────────────────────────────────

export function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
      URL.revokeObjectURL(url)
    }
    img.onerror = () => {
      reject(new Error(`Failed to load image: ${file.name}`))
      URL.revokeObjectURL(url)
    }
    img.src = url
  })
}

// ─── Issue Detection ────────────────────────────────────────────

export function detectIssues(
  width: number,
  height: number,
  sizeBytes: number,
  existingImages: TrainingImage[],
  fileName: string,
): ImageIssue[] {
  const issues: ImageIssue[] = []
  const shortestSide = Math.min(width, height)

  if (shortestSide < MIN_IMAGE_DIMENSION) {
    issues.push({
      type: 'too-small',
      label: `Too small (${width}x${height})`,
      fixAction: 'upscale',
      fixLabel: 'Upscale',
    })
  }

  const aspectRatio = width / height
  if (aspectRatio > 3 || aspectRatio < 1 / 3) {
    issues.push({
      type: 'bad-aspect',
      label: 'Extreme aspect ratio',
      fixAction: 'crop',
      fixLabel: 'Crop',
    })
  }

  if (height / width > 2.5) {
    issues.push({
      type: 'too-tall',
      label: 'Too tall',
      fixAction: 'crop',
      fixLabel: 'Crop',
    })
  }

  if (sizeBytes > MAX_FILE_SIZE_MB * 1024 * 1024) {
    issues.push({
      type: 'too-large-file',
      label: `Too large (${(sizeBytes / 1024 / 1024).toFixed(1)}MB)`,
      fixAction: 'remove',
      fixLabel: 'Remove',
    })
  }

  // Duplicate check (same name + size)
  const isDuplicate = existingImages.some(
    (img) => img.name === fileName && img.sizeBytes === sizeBytes,
  )
  if (isDuplicate) {
    issues.push({
      type: 'duplicate',
      label: 'Duplicate',
      fixAction: 'remove',
      fixLabel: 'Remove',
    })
  }

  return issues
}

// ─── Group Warnings ─────────────────────────────────────────────

export function groupValidationWarnings(
  images: TrainingImage[],
  dispatch: React.Dispatch<import('./types').CreateModelAction>,
): ValidationWarning[] {
  const warnings: ValidationWarning[] = []

  const tooSmall = images.filter((img) => img.issues.some((i) => i.type === 'too-small'))
  if (tooSmall.length > 0) {
    warnings.push({
      message: `You have ${tooSmall.length} too small image${tooSmall.length > 1 ? 's' : ''}. What should we do with them?`,
      imageIds: tooSmall.map((i) => i.id),
      issueType: 'too-small',
      actions: [
        {
          label: 'Outpaint',
          iconName: 'maximize-2',
          handler: () => dispatch({ type: 'DISMISS_ISSUE_BULK', payload: 'too-small' }),
        },
        {
          label: 'Upscale',
          iconName: 'arrow-up-right',
          handler: () => dispatch({ type: 'DISMISS_ISSUE_BULK', payload: 'too-small' }),
        },
      ],
    })
  }

  const badAspect = images.filter((img) => img.issues.some((i) => i.type === 'bad-aspect' || i.type === 'too-tall'))
  if (badAspect.length > 0) {
    warnings.push({
      message: `You have ${badAspect.length} image${badAspect.length > 1 ? 's' : ''} with unusual aspect ratio. What should we do?`,
      imageIds: badAspect.map((i) => i.id),
      issueType: 'bad-aspect',
      actions: [
        {
          label: 'Outpaint',
          iconName: 'maximize-2',
          handler: () => {
            dispatch({ type: 'DISMISS_ISSUE_BULK', payload: 'bad-aspect' })
            dispatch({ type: 'DISMISS_ISSUE_BULK', payload: 'too-tall' })
          },
        },
        {
          label: 'Crop',
          iconName: 'crop',
          handler: () => {
            dispatch({ type: 'DISMISS_ISSUE_BULK', payload: 'bad-aspect' })
            dispatch({ type: 'DISMISS_ISSUE_BULK', payload: 'too-tall' })
          },
        },
      ],
    })
  }

  const hasText = images.filter((img) => img.issues.some((i) => i.type === 'has-text'))
  if (hasText.length > 0) {
    warnings.push({
      message: `You have ${hasText.length} image${hasText.length > 1 ? 's' : ''} with text. It poorly affects AI training.`,
      imageIds: hasText.map((i) => i.id),
      issueType: 'has-text',
      actions: [
        {
          label: 'Keep it',
          iconName: 'check',
          handler: () => dispatch({ type: 'DISMISS_ISSUE_BULK', payload: 'has-text' }),
        },
        {
          label: 'Inpaint',
          iconName: 'paintbrush',
          handler: () => dispatch({ type: 'DISMISS_ISSUE_BULK', payload: 'has-text' }),
        },
      ],
    })
  }

  return warnings
}

// ─── Cost Calculation ───────────────────────────────────────────

export function calculateCost(trainer: TrainingModelDef, steps: number, walletBalance: number = 0): CostEstimate {
  const providerCost = steps * trainer.pricePerUnit
  const platformFee = providerCost * (MARKUP_MULTIPLIER - 1)
  const totalCost = providerCost * MARKUP_MULTIPLIER
  const remainingBalance = walletBalance - totalCost

  return {
    providerCost,
    platformFee,
    totalCost,
    walletBalance,
    remainingBalance,
    estimatedDuration: estimateDuration(trainer, steps),
    hasSufficientFunds: remainingBalance >= 0,
  }
}

function estimateDuration(trainer: TrainingModelDef, steps: number): string {
  const minutesPer100: Record<string, number> = {
    flux: 1,
    qwen: 0.5,
    'z-image': 0.5,
    wan: 3,
    ltx: 2,
    hunyuan: 3,
  }
  const rate = minutesPer100[trainer.family] ?? 1
  const totalMinutes = Math.round((steps / 100) * rate)

  if (totalMinutes < 1) return '<1 min'
  if (totalMinutes < 60) return `~${totalMinutes} min`
  const hours = Math.floor(totalMinutes / 60)
  const mins = totalMinutes % 60
  return mins > 0 ? `~${hours}h ${mins}m` : `~${hours}h`
}

// ─── Can Submit Check ───────────────────────────────────────────

export function canSubmitTraining(state: CreateModelState, trainer: TrainingModelDef | null, walletBalance: number = 0): boolean {
  if (!state.modelName.trim()) return false
  if (state.images.length < 4) return false
  if (!trainer) return false
  if (state.submitStatus !== 'idle' && state.submitStatus !== 'error') return false
  if (state.steps < trainer.minSteps) return false

  const cost = calculateCost(trainer, state.steps, walletBalance)
  if (!cost.hasSufficientFunds) return false

  return true
}
